/**
 * Email Sections Service
 * Manages email recipient configuration per email type.
 * Stored in Firestore at: Utils/Emails/Sections/{sectionId}
 */
import { getFirebaseDb } from '@core/firebase/firebase.js'
import { doc, getDoc, getDocs, setDoc, collection } from 'firebase/firestore'

/** Default section IDs with Hebrew descriptions */
export const EMAIL_SECTIONS = {
  newClientAgent: 'אימייל על יצירת לקוח חדש על ידי סוכן',
  updateClientAgent: 'אימייל על עדכון פרטי לקוח על ידי סוכן',
  vacationRequest: 'אימייל על בקשת חופשה של סוכן',
  routeChangeRequest: 'אימייל על בקשת שינוי מסלול',
  b2bJoinForm: 'אימייל על בקשת הצטרפות לרשת (B2B)',
  appTransfer: 'אימייל על בקשת העברת לקוח לאפליקציה',
  assortmentRequest: 'אימייל על בקשת שינוי מגוון ללקוח',
  paymentTermsRequest: 'אימייל על בקשה לשינוי תנאי תשלום',
  cashPaymentConfirm: 'אימייל אישור תשלום מזומן',
  creditPaymentConfirm: 'אימייל אישור תשלום כ.אשראי',
  walletPackageDelivery: 'אימייל על מסירת חבילות קופה',
  refundNotification: 'אימייל על זיכוי',
  preSaleOrder: 'אימייל הזמנת pre sale',
}

const SECTIONS_PATH = 'Utils/Emails/Sections'

/**
 * Get a single email section document
 * @param {string} sectionId
 * @returns {Promise<{description: string, recipients: string[]} | null>}
 */
export async function getEmailSection(sectionId) {
  const db = getFirebaseDb()
  const snap = await getDoc(doc(db, SECTIONS_PATH, sectionId))
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() }
}

/**
 * Get recipients array for a section (returns [] if not found)
 * @param {string} sectionId
 * @returns {Promise<string[]>}
 */
export async function getEmailRecipients(sectionId) {
  const section = await getEmailSection(sectionId)
  if (!section || !Array.isArray(section.recipients)) return []
  return section.recipients
}

/**
 * Get all email section documents
 * @returns {Promise<Array<{id: string, description: string, recipients: string[]}>>}
 */
export async function getAllEmailSections() {
  const db = getFirebaseDb()
  const snap = await getDocs(collection(db, SECTIONS_PATH))
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
}

/**
 * Save/update an email section document
 * @param {string} sectionId
 * @param {{ description: string, recipients: string[] }} data
 */
export async function saveEmailSection(sectionId, { description, recipients }) {
  const db = getFirebaseDb()
  await setDoc(doc(db, SECTIONS_PATH, sectionId), { description, recipients }, { merge: true })
}

/**
 * Seed default email sections if they don't exist.
 * Creates each section with yavrosman@gmail.com as default recipient.
 */
export async function seedEmailSections() {
  const db = getFirebaseDb()
  for (const [sectionId, description] of Object.entries(EMAIL_SECTIONS)) {
    const ref = doc(db, SECTIONS_PATH, sectionId)
    const snap = await getDoc(ref)
    if (!snap.exists()) {
      await setDoc(ref, {
        description,
        recipients: ['yavrosman@gmail.com'],
      })
    }
  }
}
